import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { useUnityBuildStore } from '../../stores/store';
import {
  useCreateEstimateMutation,
  useUpdateEstimateMutation,
} from '../../hooks/useCreateProjectEstimateMutation';
import { useStore } from 'zustand';
import { Estimate } from '../../api/protosCompiled/projectEstimate/projectEstimate_pb';
import { useFetchEstimates, useFetchEstimateStatuses } from '../../hooks/useFetchProjectEstimates';
import { useGetMasterFormatDivision } from '../../hooks/useGetMasterFormatDivision';
import { useFetchContracts } from '../../hooks/useFetchContracts';
import {
  MenuItem,
  Select,
  Tooltip,
  Typography,
  CircularProgress,
  Backdrop,
  FormControl,
  InputLabel,
  Alert,
  Paper,
  useTheme,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { MasterFormatDivision } from '../../api/protosCompiled/masterFormatDivision/masterFormatDivision_pb';
import InstructionDocuments from '../instructions/InstructionDocuments';
import { instructionData } from '../instructions/instructionData';
import { useFetchAssembliesByEstimateId } from '../../hooks/useFetchAssembliesByEstimateId';
import { isValidTakeoffVariable } from '../assembliesAndItems/assemblies/CreateAssembly';
import { acceptedEstimateId } from '../../utils/useLockBid';

// Helper function to get brief status descriptions for MenuItems
const getStatusMenuDescription = (statusName: string): string => {
  const statusLower = statusName.toLowerCase();

  switch (statusLower) {
    case 'draft':
      return 'Still developing';
    case 'pending':
      return 'Ready for review';
    case 'rejected':
      return 'Client declined bid';
    case 'declined':
      return 'Not pursuing';
    case 'void':
      return 'No longer valid';
    case 'accepted':
      return 'Won the bid';
    default:
      return '';
  }
};

// Helper function to format date to "yyyy-MM-dd"
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toISOString().split('T')[0]; // Extract only the date part
};

// Type guard to check if an object is an Estimate.AsObject
function isEstimateObject(obj: any): obj is Estimate.AsObject {
  return obj && typeof obj === 'object' && 'estimateid' in obj;
}

export const CreateEstimate: React.FC = () => {
  const theme = useTheme();
  const isCreatingEstimate = useStore(useUnityBuildStore).isCreatingEstimate;
  const selectedEstimate = useStore(useUnityBuildStore)
    .selectedEstimate as Estimate.AsObject | null;
  const selectedProject = useStore(useUnityBuildStore).selectedProject;
  const setIsCreatingEstimate = useStore(useUnityBuildStore).setIsCreatingEstimate;
  const setIsViewingEstimateForm = useStore(useUnityBuildStore).setIsViewingEstimateForm;

  // Default template for work description
  const getDefaultWorkDescriptionTemplate = (): string => {
    return `
      <p><strong>We include installation of the following:</strong></p>
      <ol>
        <li>Inclusion 1</li>
        <li>Inclusion 2</li>
      </ol>
      <p><strong>We exclude the following:</strong></p>
      <ol>
        <li>Exclusion 1</li>
        <li>Exclusion 2</li>
      </ol>
      <p><strong>The following are included as alternates (VE):</strong></p>
      <ol>
        <li>Alternate 1</li>
        <li>Alternate 2</li>
      </ol>
    `;
  };

  // Helper function to safely render HTML content
  const renderHtmlContent = (htmlString: string | undefined | null): React.ReactNode => {
    // If no content provided, show default template
    // Check for null, undefined, empty string, or only whitespace
    const hasNoContent =
      !htmlString ||
      htmlString === '' ||
      (typeof htmlString === 'string' && htmlString.trim() === '') ||
      (typeof htmlString === 'string' && htmlString.replace(/\s+/g, '') === '');

    if (hasNoContent) {
      const defaultTemplate = getDefaultWorkDescriptionTemplate();
      return (
        <span
          dangerouslySetInnerHTML={{ __html: defaultTemplate }}
          style={{
            wordBreak: 'break-word',
            lineHeight: 1.5,
            opacity: 0.7,
            fontStyle: 'italic',
          }}
        />
      );
    }

    // Remove common HTML entities and clean up the content
    const cleanedHtml = htmlString
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // Check if content contains HTML tags
    const containsHtml = /<[^>]*>/g.test(cleanedHtml);

    if (containsHtml) {
      return (
        <span
          dangerouslySetInnerHTML={{ __html: cleanedHtml }}
          style={{
            wordBreak: 'break-word',
            lineHeight: 1.5,
          }}
        />
      );
    }

    // If no HTML tags, return as plain text
    return cleanedHtml;
  };

  const { data: masterFormatDivision } = useGetMasterFormatDivision();
  const { data: estimateStatuses } = useFetchEstimateStatuses();
  const { data: contracts } = useFetchContracts();

  // Fetch assemblies for the selected estimate
  const { data: assemblies } = useFetchAssembliesByEstimateId(selectedEstimate?.estimateid || '');

  // Add loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [formInitialized, setFormInitialized] = useState(false);

  // State for instruction dialog
  const [showInstructionDialog, setShowInstructionDialog] = useState(false);
  const [instructionIndex, setInstructionIndex] = useState(0);

  // Function to open instruction dialog to "Estimates" section
  const openEstimatesInstructions = () => {
    const estimatesIndex = instructionData.findIndex(item => item.title === 'Estimates');
    setInstructionIndex(estimatesIndex >= 0 ? estimatesIndex : 0);
    setShowInstructionDialog(true);
  };

  // Get the mutation hooks
  const createEstimateMutation = useCreateEstimateMutation();
  const updateEstimateMutation = useUpdateEstimateMutation();

  const [description, setDescription] = useState('');
  const [duedate, setDueDate] = useState<string>(formatDate(new Date().toISOString()));
  const [name, setName] = useState('');
  const [customid, setCustomId] = useState('');
  const [selectedDivision, setSelectedDivision] = useState<MasterFormatDivision.AsObject | null>(
    null
  );
  const [selectedStatus, setSelectedStatus] = useState<{
    estimatestatusid: string;
    estimatestatusname: string;
    estimatestatusdescription: string;
  } | null>(null);

  // Validation state
  const [isValid, setIsValid] = useState<boolean>(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [invalidAssemblies, setInvalidAssemblies] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<{
    customId?: string;
    submit?: string;
  }>({});

  // Helper function to check if estimate is associated with a contract
  const isEstimateInContract = (estimateId: string): boolean => {
    return (
      contracts?.some(contract =>
        contract.associatedestimatesList?.some(estimate => estimate.estimateid === estimateId)
      ) || false
    );
  };

  // Check if status changes should be restricted
  const isStatusChangeRestricted = Boolean(
    selectedEstimate &&
      selectedEstimate.estimatestatusid === acceptedEstimateId &&
      isEstimateInContract(selectedEstimate.estimateid)
  );

  // Effect to initialize form with selected estimate data
  useEffect(() => {
    // For creating new estimate, just reset the form
    if (isCreatingEstimate) {
      resetForm();
      return;
    }

    // For editing an estimate, populate with existing data
    if (selectedEstimate && isEstimateObject(selectedEstimate)) {
      setDescription(selectedEstimate.description || '');
      setDueDate(formatDate(selectedEstimate.duedate || new Date().toISOString()));
      setName(selectedEstimate.name || '');
      setCustomId(selectedEstimate.customid || '');
      setSelectedDivision(selectedEstimate.masterformatdivision || null);
      setSelectedStatus({
        estimatestatusid: selectedEstimate.estimatestatusid || '',
        estimatestatusname: selectedEstimate.estimatestatusname || '',
        estimatestatusdescription: selectedEstimate.estimatestatusdescription || '',
      });
    }
  }, [isCreatingEstimate, selectedEstimate]);

  // Run validation whenever assemblies change
  useEffect(() => {
    if (!isCreatingEstimate && assemblies && assemblies.length > 0) {
      validateEstimateAssemblies();
    } else {
      // No assemblies to validate
      setIsValid(true);
      setValidationError(null);
      setInvalidAssemblies([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assemblies, isCreatingEstimate]);

  // Validate assemblies and their items for invalid takeoff variables
  const validateEstimateAssemblies = (): boolean => {
    if (!assemblies || assemblies.length === 0) {
      // No assemblies to validate
      setIsValid(true);
      setValidationError(null);
      setInvalidAssemblies([]);
      return true;
    }

    let hasInvalidVariables = false;
    const foundInvalidAssemblies: string[] = [];

    // Check each takeoff assembly
    assemblies.forEach(takeoffAssembly => {
      // Each takeoffAssembly has an assembliesList property
      takeoffAssembly.assembliesList?.forEach(assembly => {
        if (!assembly) return;

        const measurementType = assembly.assemblymeasurementtype;
        const assemblyName = assembly.assemblyname || 'Unnamed Assembly';

        // Check each item in the assembly
        assembly.itemsList?.forEach(item => {
          if (!item.itemqtyformula) return;

          const takeoffVar = item.itemqtyformula.takeoffvariabletype;
          if (!isValidTakeoffVariable(takeoffVar, measurementType)) {
            hasInvalidVariables = true;
            if (!foundInvalidAssemblies.includes(assemblyName)) {
              foundInvalidAssemblies.push(assemblyName);
            }
          }
        });
      });
    });

    setInvalidAssemblies(foundInvalidAssemblies);

    if (hasInvalidVariables) {
      const assemblyList = foundInvalidAssemblies.join(', ');
      setValidationError(
        `Invalid takeoff variables found in these assemblies: ${assemblyList}. 
        This can lead to inaccurate estimates.`
      );
      setIsValid(false);
      return false;
    }

    setIsValid(true);
    setValidationError(null);
    return true;
  };

  // Effect to set default status for new estimates
  useEffect(() => {
    if (isCreatingEstimate && estimateStatuses && estimateStatuses.length > 0 && !selectedStatus) {
      // Set the first status as default for new estimates
      const defaultStatus = estimateStatuses[0];
      setSelectedStatus({
        estimatestatusid: defaultStatus.estimatestatusid,
        estimatestatusname: defaultStatus.estimatestatusname,
        estimatestatusdescription: defaultStatus.estimatestatusdescription,
      });
    }
  }, [isCreatingEstimate, estimateStatuses, selectedStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Ensure all fields are valid
    if (!selectedDivision) {
      alert('Please select a Master Format Division.');
      return;
    }

    if (!selectedStatus) {
      alert('Please select an Estimate Status.');
      return;
    }

    // Don't allow submission if validation fails
    if (!isCreatingEstimate && !isValid) {
      alert('Cannot save estimate with invalid takeoff variables. Please fix the issues first.');
      return;
    }

    const formData: Estimate.AsObject = {
      estimateid: selectedEstimate?.estimateid ?? '',
      description: description,
      duedate: duedate,
      projectid: selectedProject?.id || '',
      datecreated: selectedEstimate?.datecreated ?? new Date().toISOString(),
      name: name,
      customid: customid,
      createdbyuserid: selectedEstimate?.createdbyuserid ?? '',
      createdbyaccountid: selectedEstimate?.createdbyaccountid ?? '',
      lastupdated: new Date().toISOString(),
      masterformatdivision: selectedDivision,
      estimatestatusid: selectedStatus?.estimatestatusid || '',
      estimatestatusname: selectedStatus?.estimatestatusname || '',
      estimatestatusdescription: '',
      sellingprice: selectedEstimate?.sellingprice ?? 0,
    };

    // Clear any previous submit errors
    setFormErrors(prev => ({ ...prev, submit: undefined }));

    // Set loading state
    setIsSubmitting(true);

    try {
      // Save the estimate
      if (isCreatingEstimate) {
        await createEstimateMutation.mutateAsync(formData);
      } else {
        await updateEstimateMutation.mutateAsync(formData);
      }
      setIsViewingEstimateForm(false);
      resetForm();
    } catch (error: any) {
      console.error('Error saving estimate:', error);

      // Check for duplicate ID constraint violation
      if (error?.message?.includes('unique constraint "unique_estimate_custom_id_account_id"')) {
        setFormErrors(prev => ({
          ...prev,
          customId: 'This Estimate ID is already in use. Please choose a different ID.',
        }));
      } else {
        // Generic error message for other errors
        setFormErrors(prev => ({
          ...prev,
          submit: 'An error occurred while saving the estimate. Please try again.',
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEstimateFormCancel = () => {
    setIsCreatingEstimate(false);
    setIsViewingEstimateForm(false);
  };

  const resetForm = () => {
    setDescription('');
    setDueDate(formatDate(new Date().toISOString()));
    setName('');
    setCustomId('');
    setSelectedDivision(null);
    setSelectedStatus(null);
    setValidationError(null);
    setInvalidAssemblies([]);
    setIsValid(true);
    setFormErrors({});
  };

  // Determine if the save button should be disabled
  const isSaveButtonDisabled = (!isCreatingEstimate && !isValid) || isSubmitting;

  // Generate tooltip message for disabled save button
  const getTooltipMessage = () => {
    if (isSaveButtonDisabled && !isSubmitting) {
      return (
        <>
          <Typography variant="body2" fontWeight="bold">
            Cannot save estimate due to invalid takeoff variables
          </Typography>
          <Typography variant="body2">
            The following assemblies have invalid takeoff variables:
          </Typography>
          <ul style={{ paddingLeft: '16px', margin: '4px 0' }}>
            {invalidAssemblies.map((assembly, index) => (
              <li key={index}>{assembly}</li>
            ))}
          </ul>
          <Typography variant="body2">Valid takeoff variable types:</Typography>
          <ul style={{ paddingLeft: '16px', margin: '4px 0' }}>
            <li>Count assemblies: point only</li>
            <li>Length assemblies: point, segment, length</li>
            <li>Area assemblies: all types</li>
          </ul>
          <Typography variant="body2">Please fix these issues before saving.</Typography>
        </>
      );
    }
    return '';
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
      {/* Estimate Guidance */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              📊 Creating an Estimate
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Estimates are for the <strong>bid phase</strong> - before a contract is awarded. Use
              this form when competing for projects where you may or may not win the work.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>What this form is for:</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ ml: 2, mb: 1 }}>
              • Creating competitive bids and proposals
              <br />
              • Estimating project costs before contract award
              <br />• Using status fields to communicate bid progress to your team
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
              ⚠️ Need a Change Order instead?
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              For modifications to existing contracts, use the{' '}
              <strong>Contract Management System</strong>. Estimates cannot be converted to change
              orders.
            </Typography>
          </Box>
          <Tooltip title="Learn more about Estimates">
            <IconButton
              onClick={openEstimatesInstructions}
              size="small"
              sx={{ ml: 1, color: 'info.main' }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Alert>

      <TextField
        margin="normal"
        required
        fullWidth
        id="name"
        label="Estimate Name"
        rows={4}
        value={name}
        onChange={e => setName(e.target.value)}
      />
      <FormControl fullWidth required sx={{ mt: 2 }}>
        <InputLabel id="division-label">Division</InputLabel>
        <Select
          labelId="division-label"
          value={selectedDivision?.id || ''}
          label="Division"
          onChange={e => {
            const selected = masterFormatDivision?.find(division => division.id === e.target.value);
            setSelectedDivision(selected || null);
          }}
        >
          <MenuItem value="" disabled>
            <em>Select Division</em>
          </MenuItem>
          {masterFormatDivision?.map(division => (
            <MenuItem key={division.id} value={division.id}>
              {`${division.divisionnumber} - ${division.divisionname}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Alert when status changes are restricted */}
      {isStatusChangeRestricted && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Status Locked:</strong> This estimate has been accepted and is associated with a
            contract. The status cannot be changed to maintain contract integrity.
          </Typography>
        </Alert>
      )}

      <FormControl fullWidth required sx={{ mt: 2 }}>
        <InputLabel id="status-label">Status</InputLabel>
        <Select
          labelId="status-label"
          value={selectedStatus?.estimatestatusid || ''}
          label="Status"
          disabled={isStatusChangeRestricted}
          onChange={e => {
            const selected = estimateStatuses?.find(
              status => status.estimatestatusid === e.target.value
            );
            setSelectedStatus(
              selected
                ? {
                    estimatestatusid: selected.estimatestatusid,
                    estimatestatusname: selected.estimatestatusname,
                    estimatestatusdescription: selected.estimatestatusdescription,
                  }
                : null
            );
          }}
        >
          <MenuItem value="" disabled>
            <em>Select Status</em>
          </MenuItem>
          {estimateStatuses?.map(status => {
            const description = getStatusMenuDescription(status.estimatestatusname);
            return (
              <MenuItem key={status.estimatestatusid} value={status.estimatestatusid}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span>{status.estimatestatusname}</span>
                  {description && (
                    <span style={{ opacity: 0.7, fontSize: '0.85em', fontStyle: 'italic' }}>
                      {description}
                    </span>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {/* Estimate Description - HTML-enabled display */}
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ mb: 1, fontSize: '0.75rem', fontWeight: 500 }}
        >
          Estimate Description
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            minHeight: 100,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.action.disabled
                : theme.palette.grey[100],
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            '& p': { margin: '8px 0' },
            '& strong': { fontWeight: 600 },
            '& em': { fontStyle: 'italic' },
            '& ul, & ol': { paddingLeft: '20px', margin: '8px 0' },
            '& li': { margin: '4px 0' },
          }}
        >
          <Typography component="div" variant="body1">
            {renderHtmlContent(description)}
          </Typography>
        </Paper>
      </Box>
      <TextField
        margin="normal"
        fullWidth
        id="customId"
        label="Estimate ID"
        value={customid}
        onChange={e => {
          setCustomId(e.target.value);
          // Clear errors when user starts typing
          if (formErrors.customId || formErrors.submit) {
            setFormErrors(prev => ({ ...prev, customId: undefined, submit: undefined }));
          }
        }}
        error={!!formErrors.customId}
        helperText={formErrors.customId}
      />
      <TextField
        margin="normal"
        fullWidth
        id="dueDate"
        label="Due Date"
        type="date"
        InputLabelProps={{
          shrink: true,
        }}
        value={duedate}
        onChange={e => setDueDate(e.target.value)}
      />

      {/* Display validation error message if exists */}
      {validationError && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {validationError}
        </Typography>
      )}

      {/* Display submit error */}
      {formErrors.submit && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {formErrors.submit}
        </Alert>
      )}

      <Tooltip
        title={getTooltipMessage()}
        placement="top"
        arrow
        disableHoverListener={!isSaveButtonDisabled || isSubmitting}
      >
        <span>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 2, mb: 2 }}
            disabled={isSaveButtonDisabled}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
                {isCreatingEstimate ? 'Creating Estimate...' : 'Updating Estimate...'}
              </>
            ) : isCreatingEstimate ? (
              'Create Estimate'
            ) : (
              'Update Estimate'
            )}
          </Button>
        </span>
      </Tooltip>

      <Button onClick={handleEstimateFormCancel} disabled={isSubmitting}>
        Cancel
      </Button>

      {/* Loading backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={isSubmitting}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {isCreatingEstimate ? 'Creating Estimate...' : 'Updating Estimate...'}
          </Typography>
        </Box>
      </Backdrop>

      {/* Instruction Dialog */}
      <Dialog
        open={showInstructionDialog}
        onClose={() => setShowInstructionDialog(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6">Estimates Guide</Typography>
          <IconButton onClick={() => setShowInstructionDialog(false)} size="small">
            <InfoIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <InstructionDocuments
            subjects={instructionData}
            currentIndex={instructionIndex}
            onNavigate={setInstructionIndex}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};
